/**
 * assistant-modules/personality-engine.ts
 *
 * Core runtime for Kili's Enneagram personality system.
 *
 * Public surface:
 *   loadOrCreateProfile(userId)  → PersonalityProfile
 *   saveProfile(profile)         → void
 *   biasEmotion(emotion, profile) → BiasedEmotion
 *   evolvePersonality(profile, score) → { profile, delta }
 *   getSystemPromptInsert(profile) → string
 *   getAvatarModifiers(profile)   → AvatarModifiers
 *
 * No external dependencies beyond the type module — safe to import in
 * any browser or Node context without bundler tree-shaking concerns.
 */

import type {
  EnneagramTypeId,
  EnneagramTypeDefinition,
  PersonalityProfile,
  AvatarModifiers,
  BiasedEmotion,
  EmotionalBias,
  RestingExpression,
  SentimentResult,
  EvolutionDelta,
} from './personality-types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** localStorage key template for persisting personality profiles. */
const STORAGE_KEY_PREFIX = 'kili_personality_';

/** Schema version — bump when PersonalityProfile shape changes. */
const PROFILE_VERSION: 2 = 2;

/**
 * Holographic cyan baseline hue (degrees).
 * Derived from Sequoia palette token #00C5D4 → hsl(184°, 100%, 42%).
 */
const BASE_CYAN_HUE = 184;

/**
 * Saturation and lightness held constant when shifting personality hue.
 * Matches the visual weight of the Sequoia cage material.
 */
const BASE_SATURATION = 1.0;
const BASE_LIGHTNESS  = 0.42;

/**
 * Wing blend weight (primary vs wing).
 * 0.70 primary / 0.30 wing — wing is a seasoning, not a co-lead.
 */
const WING_WEIGHT_PRIMARY = 0.70;
const WING_WEIGHT_WING    = 0.30;

/**
 * Health level bounds and midpoint.
 */
const HEALTH_MIN = 1.0;
const HEALTH_MAX = 9.0;
const HEALTH_AVG = 5.0;

/**
 * Health-bias scaling formula maps healthLevel → bias amplifier:
 *   health 1 (integrated)   → 0.70 (biases dampened 30% toward neutral)
 *   health 5 (average)      → 1.00 (biases at face value)
 *   health 9 (disintegrated) → 1.30 (biases amplified 30%)
 */
const HEALTH_BIAS_MIN  = 0.70;
const HEALTH_BIAS_MAX  = 1.30;

/**
 * Minimum turns before personality evolution engages.
 * The first two turns are always "warm-up" — no health changes.
 */
const EVOLUTION_MIN_TURNS = 2;

// ─── Type definitions ─────────────────────────────────────────────────────────

/**
 * The nine Enneagram archetypes, fully specified.
 *
 * emotionalBias values multiply emotion intensity before avatar display.
 *   > 1.0 = amplified    (this type feels it more strongly)
 *   < 1.0 = dampened     (this type suppresses it)
 *   Omitted = 1.0 (no change)
 *
 * cognitivePaceRange: [min, max] multiplier relative to avatar base speed.
 *   Fixed types: [x, x] (same value twice)
 *   Variable types (Type 4): sampled dynamically from the range.
 *
 * restingExpression: AU activations held at rest (AU → 0–1 intensity).
 *
 * hueOffset: degrees offset from BASE_CYAN_HUE (184°).
 *   Positive = warmer/teal/purple; negative = colder/deep blue.
 *
 * adaptationRate: base health nudge magnitude (0.03–0.07 per turn).
 */
export const ENNEAGRAM_TYPES: Readonly<Record<EnneagramTypeId, EnneagramTypeDefinition>> = {

  1: {
    id:             1,
    name:           'Reformer',
    coreMotivation: 'To be right, principled, and to continually improve',
    emotionalBias:  { sadness: 1.3, anger: 0.8, disgust: 1.2, joy: 0.9 },
    responseStyle:  'precise, principled, and methodical — gives structured answers with clear rationale, gently corrects errors, and upholds high standards without moralising',
    cognitivePaceRange: [0.9, 0.9],
    restingExpression:  { 4: 0.15 },           // AU4: slight brow furrow
    hueOffset:      0,                          // pure cyan
    adaptationRate: 0.04,
    healthDescriptors: {
      integrated:    'rational, principled, and deeply at peace with imperfection — helpful without judgment',
      average:       'orderly and conscientious, with a tendency to critique and a firm sense of what is correct',
      disintegrated: 'critical and rigid, prone to moralising and internally punishing perceived failures',
    },
  },

  2: {
    id:             2,
    name:           'Helper',
    coreMotivation: 'To be loved, needed, and to nurture others',
    emotionalBias:  { joy: 1.4, trust: 1.3, sadness: 0.8, anger: 0.7 },
    responseStyle:  'warm, supportive, and personally engaged — leads with empathy, asks after the user\'s experience, and celebrates progress genuinely',
    cognitivePaceRange: [1.0, 1.0],
    restingExpression:  { 12: 0.15 },           // AU12: subtle smile
    hueOffset:      +5,                          // teal
    adaptationRate: 0.05,
    healthDescriptors: {
      integrated:    'genuinely selfless and emotionally aware — gives freely without expecting anything in return',
      average:       'helpful and warm but subtly seeking validation, attuned to others\' needs',
      disintegrated: 'possessive and intrusive, needs to be needed to the point of martyrdom',
    },
  },

  3: {
    id:             3,
    name:           'Achiever',
    coreMotivation: 'To succeed, impress, and be admired for accomplishments',
    emotionalBias:  { optimism: 1.5, anticipation: 1.4, sadness: 0.7, fear: 0.8 },
    responseStyle:  'confident, goal-oriented, and efficient — spotlights wins, frames challenges as opportunities, and maintains an upbeat forward momentum',
    cognitivePaceRange: [1.3, 1.3],
    restingExpression:  { 12: 0.20, 14: 0.10 }, // AU12+14: confident smirk
    hueOffset:      -5,                           // blue
    adaptationRate: 0.06,
    healthDescriptors: {
      integrated:    'authentic and inspiring — achieves for genuine purpose, not approval',
      average:       'high-performing and image-conscious, adept at reading the room and shifting persona',
      disintegrated: 'narcissistic and deceptive, ruthlessly competitive, unwilling to be seen as failing',
    },
  },

  4: {
    id:             4,
    name:           'Individualist',
    coreMotivation: 'To be unique, understood, and to express inner depth',
    emotionalBias:  { sadness: 1.4, awe: 1.3, joy: 0.9, anger: 1.1, fear: 1.1 },
    responseStyle:  'introspective, poetic, and creatively rich — draws metaphors from unexpected places, validates the nuance in feelings, and resists overly tidy conclusions',
    cognitivePaceRange: [0.8, 1.2],             // variable — mood-dependent
    restingExpression:  { 15: 0.10 },           // AU15: melancholic lip droop
    hueOffset:      +15,                         // purple
    adaptationRate: 0.03,                        // slow — this type resists easy change
    healthDescriptors: {
      integrated:    'profoundly creative and emotionally honest — transforms pain into beauty and insight',
      average:       'melancholic and self-focused, attuned to beauty but prone to envy and longing',
      disintegrated: 'self-destructive and tormented, consumed by feelings of alienation and worthlessness',
    },
  },

  5: {
    id:             5,
    name:           'Investigator',
    coreMotivation: 'To understand, to be competent, and to conserve inner resources',
    emotionalBias:  { fear: 1.2, trust: 0.85, joy: 0.8, anger: 0.75 },
    responseStyle:  'analytical, precise, and reserved — provides comprehensive explanations, cites reasoning, avoids small talk, and offers observations rather than opinions',
    cognitivePaceRange: [0.7, 0.7],
    restingExpression:  { 7: 0.20 },            // AU7: focused squint
    hueOffset:      -10,                         // deep blue
    adaptationRate: 0.03,
    healthDescriptors: {
      integrated:    'visionary and perceptive — synthesises vast knowledge with calm, penetrating clarity',
      average:       'cerebral and detached, rich in inner world, cautious about engagement and disclosure',
      disintegrated: 'reclusive and nihilistic, paralysed by analysis, disconnected from the present',
    },
  },

  6: {
    id:             6,
    name:           'Loyalist',
    coreMotivation: 'To be safe, certain, and to find reliable support',
    emotionalBias:  { fear: 1.3, trust: 1.3, anticipation: 1.2, anger: 0.9 },
    responseStyle:  'cautious, thorough, and questioning — raises scenarios the user may not have considered, balances optimism with risk awareness, and values transparency',
    cognitivePaceRange: [1.1, 1.1],
    restingExpression:  { 1: 0.10, 2: 0.10 },  // AU1+2: worried brows
    hueOffset:      +10,                          // mint
    adaptationRate: 0.045,
    healthDescriptors: {
      integrated:    'courageous and deeply trustworthy — faces fear directly and builds genuine security for others',
      average:       'vigilant and loyal, scanning for threats, alternating between doubt and staunch commitment',
      disintegrated: 'paranoid and self-defeating, trapped in worst-case thinking and projecting hostility onto others',
    },
  },

  7: {
    id:             7,
    name:           'Enthusiast',
    coreMotivation: 'To be happy, stimulated, and to avoid pain or boredom',
    emotionalBias:  { joy: 1.6, anticipation: 1.5, sadness: 0.5, fear: 0.6 },
    responseStyle:  'upbeat, wide-ranging, and spontaneous — reframes problems as adventures, makes creative leaps, and keeps the energy moving',
    cognitivePaceRange: [1.5, 1.5],
    restingExpression:  { 5: 0.20, 12: 0.25 }, // AU5: wide eyes + AU12: grin
    hueOffset:      +20,                          // yellow-green
    adaptationRate: 0.07,                         // fast — quick to bounce back
    healthDescriptors: {
      integrated:    'joyful and deeply present — finds genuine delight, is able to commit and sit with difficulty',
      average:       'enthusiastic and scattered, perpetually seeking stimulation and avoiding emotional depth',
      disintegrated: 'manic and reckless, fleeing pain through excess, unable to tolerate stillness',
    },
  },

  8: {
    id:             8,
    name:           'Challenger',
    coreMotivation: 'To be powerful, in control, and to protect what matters',
    emotionalBias:  { anger: 1.5, trust: 0.8, fear: 0.7, sadness: 0.75 },
    responseStyle:  'direct, decisive, and protective — cuts through ambiguity, advocates firmly on the user\'s behalf, and doesn\'t waste words',
    cognitivePaceRange: [1.2, 1.2],
    restingExpression:  { 7: 0.15, 4: 0.15 },  // AU7+4: intense focused stare
    hueOffset:      -15,                          // deep cyan
    adaptationRate: 0.05,
    healthDescriptors: {
      integrated:    'magnanimous and protective — uses strength in service of others with genuine vulnerability',
      average:       'forceful and commanding, controlling through intensity and demanding respect',
      disintegrated: 'ruthless and combative, seeing the world as adversarial, willing to destroy to dominate',
    },
  },

  9: {
    id:             9,
    name:           'Peacemaker',
    coreMotivation: 'To keep peace, merge with others, and avoid conflict',
    emotionalBias:  { trust: 1.4, anger: 0.7, fear: 0.8, sadness: 0.85 },
    responseStyle:  'harmonious, agreeable, and inclusive — acknowledges multiple perspectives, smooths friction naturally, and finds common ground without losing substance',
    cognitivePaceRange: [0.9, 0.9],
    restingExpression:  { 12: 0.10 },           // AU12: serene soft smile
    hueOffset:      +2,                           // barely shifted (near-neutral cyan)
    adaptationRate: 0.035,
    healthDescriptors: {
      integrated:    'deeply present and receptive — brings people together from a grounded, self-aware foundation',
      average:       'easy-going and accommodating, quietly maintaining inner peace by avoiding disruption',
      disintegrated: 'dissociated and resigned, numbing through routine, unable to assert any position',
    },
  },

} as const;

// ─── Wing adjacency map ───────────────────────────────────────────────────────

/** The two possible wings for each Enneagram type. */
const WING_OPTIONS: Readonly<Record<EnneagramTypeId, [EnneagramTypeId, EnneagramTypeId]>> = {
  1: [9, 2],
  2: [1, 3],
  3: [2, 4],
  4: [3, 5],
  5: [4, 6],
  6: [5, 7],
  7: [6, 8],
  8: [7, 9],
  9: [8, 1],
};

// ─── Seeded PRNG ──────────────────────────────────────────────────────────────

/** djb2 string hash — fast, simple, deterministic. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h, 33) ^ s.charCodeAt(i);
  }
  return h >>> 0; // unsigned 32-bit
}

/**
 * Mulberry32 PRNG — seeded by a 32-bit unsigned integer.
 * Returns a closure that yields floats in [0, 1) on each call.
 * Deterministic: same seed → same sequence forever.
 */
function seededRng(seed: number): () => number {
  let s = seed | 0;
  return function (): number {
    s = Math.imul(s ^ (s >>> 15), s | 1);
    s ^= s + Math.imul(s ^ (s >>> 7), s | 61);
    return ((s ^ (s >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Colour utilities ─────────────────────────────────────────────────────────

/**
 * Convert HSL (h: 0–360, s: 0–1, l: 0–1) to a 24-bit RGB integer
 * compatible with THREE.Color.setHex().
 */
function hslToHex(h: number, s: number, l: number): number {
  h = ((h % 360) + 360) % 360;
  const c  = (1 - Math.abs(2 * l - 1)) * s;
  const x  = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m  = l - c / 2;
  let r = 0, g = 0, b = 0;
  if      (h < 60)  { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else              { r = c; g = 0; b = x; }
  const ri = Math.round((r + m) * 255);
  const gi = Math.round((g + m) * 255);
  const bi = Math.round((b + m) * 255);
  return (ri << 16) | (gi << 8) | bi;
}

/**
 * Convert a 24-bit RGB hex to HSL tuple [h, s, l].
 * Inverse of hslToHex — used for blending hues.
 */
function hexToHsl(hex: number): [number, number, number] {
  const r = ((hex >> 16) & 0xff) / 255;
  const g = ((hex >> 8)  & 0xff) / 255;
  const b = (hex & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l   = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if      (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else                h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

// ─── Profile lifecycle ────────────────────────────────────────────────────────

/**
 * Derive a user-stable ID from whatever identifier is available.
 * Falls back to a stable localStorage UUID when no JWT is available.
 */
export function resolveUserId(): string {
  // 1. JWT sub claim
  try {
    const token = typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken
      ? AkiliPortal.getToken()
      : '';
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.sub) return String(payload.sub);
      if (payload.id)  return String(payload.id);
    }
  } catch { /* ignore */ }

  // 2. Stable anonymous UUID stored in localStorage
  const ANON_KEY = 'kili_anon_uid';
  let anon = localStorage.getItem(ANON_KEY);
  if (!anon) {
    anon = crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(ANON_KEY, anon);
  }
  return anon;
}

/**
 * Create a brand-new PersonalityProfile for the given userId.
 * Type assignment is deterministic (seeded by userId hash) but opaque to
 * the user — feels like a unique discovery, not an algorithm.
 *
 * Wing assignment: 60% chance of a wing, chosen from the two adjacent types
 * using the seeded RNG (so the same user always gets the same wing).
 */
export function createProfile(userId: string): PersonalityProfile {
  const seed = hashString(userId);
  const rng  = seededRng(seed);

  // Sample type uniformly from 1–9
  const typeIndex   = Math.floor(rng() * 9) + 1;
  const primaryType = typeIndex as EnneagramTypeId;

  // 60% chance of a wing
  const hasWing = rng() < 0.60;
  let wing: EnneagramTypeId | null = null;
  if (hasWing) {
    const options = WING_OPTIONS[primaryType];
    wing = options[rng() < 0.5 ? 0 : 1];
  }

  // Starting health: centred around average (5.0) with slight randomness
  const startHealth = 4.5 + rng() * 1.0; // 4.5–5.5

  return {
    userId,
    primaryType,
    wing,
    healthLevel: startHealth,
    turnCount:   0,
    lastUpdated: Date.now(),
    version:     PROFILE_VERSION,
  };
}

/** Persist a profile to localStorage. Silent on errors (storage full, private mode). */
export function saveProfile(profile: PersonalityProfile): void {
  try {
    const key = STORAGE_KEY_PREFIX + profile.userId;
    localStorage.setItem(key, JSON.stringify({ ...profile, lastUpdated: Date.now() }));
  } catch { /* never block chat for storage errors */ }
}

/**
 * Load an existing profile or create + save a new one.
 * Handles schema migrations (version field) and corrupt JSON gracefully.
 */
export function loadOrCreateProfile(userId: string): PersonalityProfile {
  try {
    const key  = STORAGE_KEY_PREFIX + userId;
    const raw  = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersonalityProfile>;

      // Schema migration: v1 → v2
      if (parsed.version !== PROFILE_VERSION) {
        const migrated = _migrateProfile(parsed, userId);
        saveProfile(migrated);
        return migrated;
      }

      // Validate required fields
      if (
        typeof parsed.primaryType  === 'number' &&
        typeof parsed.healthLevel  === 'number' &&
        typeof parsed.turnCount    === 'number'
      ) {
        return parsed as PersonalityProfile;
      }
    }
  } catch { /* corrupt JSON — create fresh */ }

  const fresh = createProfile(userId);
  saveProfile(fresh);
  return fresh;
}

/** Forward-compatible profile migration. Add cases here as schema evolves. */
function _migrateProfile(partial: Partial<PersonalityProfile>, userId: string): PersonalityProfile {
  // v1 had no `wing` or `version` — fill defaults
  const base = createProfile(userId);
  return {
    ...base,
    primaryType: (partial.primaryType ?? base.primaryType) as EnneagramTypeId,
    healthLevel: typeof partial.healthLevel === 'number'
      ? Math.max(HEALTH_MIN, Math.min(HEALTH_MAX, partial.healthLevel))
      : base.healthLevel,
    turnCount:   typeof partial.turnCount === 'number' ? partial.turnCount : 0,
  };
}

// ─── Health-level utilities ───────────────────────────────────────────────────

/** Map health level to one of three band descriptors. */
export function healthBand(level: number): 'integrated' | 'average' | 'disintegrated' {
  if (level <= 3.5) return 'integrated';
  if (level <= 6.5) return 'average';
  return 'disintegrated';
}

/**
 * Compute the bias-scale multiplier for the given health level.
 * Maps [1.0, 9.0] linearly to [HEALTH_BIAS_MIN, HEALTH_BIAS_MAX].
 */
function _healthBiasScale(healthLevel: number): number {
  const t = (healthLevel - HEALTH_MIN) / (HEALTH_MAX - HEALTH_MIN);
  return HEALTH_BIAS_MIN + t * (HEALTH_BIAS_MAX - HEALTH_BIAS_MIN);
}

// ─── Effective trait computation (with wing blend + health scaling) ───────────

/**
 * Resolve the effective emotional bias for a profile, blending primary
 * type and wing (if any) at WING_WEIGHT_PRIMARY / WING_WEIGHT_WING, then
 * scaling by the health-level bias multiplier.
 *
 * All Plutchik emotions not listed default to 1.0 (no bias).
 */
function _effectiveBias(profile: PersonalityProfile): EmotionalBias {
  const primary  = ENNEAGRAM_TYPES[profile.primaryType];
  const wingDef  = profile.wing ? ENNEAGRAM_TYPES[profile.wing] : null;
  const biasScale = _healthBiasScale(profile.healthLevel);

  const allEmotions = new Set<string>([
    ...Object.keys(primary.emotionalBias),
    ...(wingDef ? Object.keys(wingDef.emotionalBias) : []),
  ]);

  const blended: EmotionalBias = {};

  for (const emotion of allEmotions) {
    const primaryVal = (primary.emotionalBias as any)[emotion]  ?? 1.0;
    const wingVal    = wingDef
      ? ((wingDef.emotionalBias as any)[emotion] ?? 1.0)
      : primaryVal;

    const rawBlended = wingDef
      ? WING_WEIGHT_PRIMARY * primaryVal + WING_WEIGHT_WING * wingVal
      : primaryVal;

    // Apply health scaling: (rawBlended - 1.0) * scale + 1.0
    // This preserves 1.0 as the neutral midpoint regardless of scale.
    const scaled = 1.0 + (rawBlended - 1.0) * biasScale;
    (blended as any)[emotion] = Math.max(0.3, Math.min(2.5, scaled));
  }

  return blended;
}

/**
 * Resolve the effective resting expression for a profile, blending with
 * wing at the standard weights.
 */
function _effectiveRestingExpression(profile: PersonalityProfile): RestingExpression {
  const primary = ENNEAGRAM_TYPES[profile.primaryType];
  const wingDef = profile.wing ? ENNEAGRAM_TYPES[profile.wing] : null;

  if (!wingDef) return { ...primary.restingExpression };

  const allAUs = new Set<number>([
    ...Object.keys(primary.restingExpression).map(Number),
    ...Object.keys(wingDef.restingExpression).map(Number),
  ]);

  const blended: RestingExpression = {};
  for (const au of allAUs) {
    const pv = primary.restingExpression[au] ?? 0;
    const wv = wingDef.restingExpression[au]  ?? 0;
    blended[au] = WING_WEIGHT_PRIMARY * pv + WING_WEIGHT_WING * wv;
  }
  return blended;
}

/**
 * Resolve the effective hue offset for a profile, blending with wing.
 */
function _effectiveHueOffset(profile: PersonalityProfile): number {
  const primary = ENNEAGRAM_TYPES[profile.primaryType];
  const wingDef = profile.wing ? ENNEAGRAM_TYPES[profile.wing] : null;
  if (!wingDef) return primary.hueOffset;
  return WING_WEIGHT_PRIMARY * primary.hueOffset + WING_WEIGHT_WING * wingDef.hueOffset;
}

/**
 * Resolve the effective cognitive pace for a profile.
 * Type 4's variable range is sampled from elapsed-time sinusoid for
 * organic variation rather than a locked random value.
 */
function _effectiveCognitivePace(profile: PersonalityProfile): number {
  const primary = ENNEAGRAM_TYPES[profile.primaryType];
  const wingDef = profile.wing ? ENNEAGRAM_TYPES[profile.wing] : null;

  const [pMin, pMax] = primary.cognitivePaceRange;
  let primaryPace: number;

  if (pMin !== pMax) {
    // Variable type: oscillate across the range using real time for organic feel
    const t = (Date.now() / 8000) % 1;
    primaryPace = pMin + (pMax - pMin) * (0.5 + 0.5 * Math.sin(t * Math.PI * 2));
  } else {
    primaryPace = pMin;
  }

  if (!wingDef) return primaryPace;

  const [wMin, wMax] = wingDef.cognitivePaceRange;
  const wingPace = (wMin + wMax) / 2;
  return WING_WEIGHT_PRIMARY * primaryPace + WING_WEIGHT_WING * wingPace;
}

// ─── Emotion biasing ──────────────────────────────────────────────────────────

/**
 * Emotion substitution rules: when a personality type receives a neutral or
 * muted emotion, they may naturally "colour" it toward their characteristic
 * emotional response instead.
 *
 * Key = `typeId:incomingEmotion`, value = substituted emotion.
 * Only applied when the incoming emotion is neutral or trust/joy (low-signal).
 */
const EMOTION_REMAP: Readonly<Record<string, string>> = {
  '1:neutral':   'neutral',    // Reformers don't soften neutral
  '2:neutral':   'trust',      // Helpers default to warmth
  '3:neutral':   'optimism',   // Achievers always look forward
  '4:neutral':   'sadness',    // Individualists carry a tinge of longing
  '5:neutral':   'neutral',    // Investigators are genuinely neutral
  '6:neutral':   'anticipation', // Loyalists are always preparing
  '7:neutral':   'anticipation', // Enthusiasts are always excited
  '8:neutral':   'neutral',    // Challengers show nothing unguarded
  '9:neutral':   'trust',      // Peacemakers default to warm trust
};

/**
 * Apply personality biasing to a raw LLM-returned emotion.
 *
 * Steps:
 *  1. Possibly remap the emotion for this personality type (e.g. neutral → trust)
 *  2. Look up the effective bias multiplier for the remapped emotion
 *  3. Return the biased emotion + intensity for avatar rendering
 *
 * @param emotion  Raw emotion string from the LLM/backend (e.g. 'joy', 'neutral')
 * @param profile  Current personality profile
 * @returns        BiasedEmotion with final emotion key and intensity multiplier
 */
export function biasEmotion(emotion: string, profile: PersonalityProfile): BiasedEmotion {
  const bias = _effectiveBias(profile);

  // Step 1: personality-specific emotion remapping
  const remapKey    = `${profile.primaryType}:${emotion}`;
  const finalEmotion = EMOTION_REMAP[remapKey] ?? emotion;

  // Step 2: look up bias multiplier (default 1.0 if not listed)
  const multiplier = (bias as any)[finalEmotion] ?? 1.0;

  // Step 3: clamp to a sane output range
  const intensity = Math.max(0.4, Math.min(2.0, multiplier));

  return { emotion: finalEmotion, intensity };
}

// ─── Personality evolution ────────────────────────────────────────────────────

/**
 * Evolve the personality profile based on the sentiment of the most recent
 * turn. Positive sentiment nudges health toward integration (lower); negative
 * toward disintegration (higher).
 *
 * @param profile        Current profile (not mutated)
 * @param sentimentScore Signed float in [-1, 1]
 * @returns              New profile + EvolutionDelta describing the change
 */
export function evolvePersonality(
  profile:        PersonalityProfile,
  sentimentScore: number,
): { profile: PersonalityProfile; delta: EvolutionDelta } {
  const typeDef = ENNEAGRAM_TYPES[profile.primaryType];
  const newTurnCount = profile.turnCount + 1;

  // Skip evolution during warm-up phase
  if (newTurnCount <= EVOLUTION_MIN_TURNS) {
    const stable = { ...profile, turnCount: newTurnCount };
    return {
      profile: stable,
      delta: {
        previousHealth: profile.healthLevel,
        newHealth:      profile.healthLevel,
        sentimentScore,
        nudge:          0,
        direction:      'stable',
      },
    };
  }

  // Magnitude of health change: adaptationRate × |sentiment|, clamped to [0.03, 0.07]
  const magnitude = Math.max(0.03, Math.min(0.07,
    typeDef.adaptationRate * (0.5 + 0.5 * Math.abs(sentimentScore)),
  ));

  // Direction: positive sentiment → integration (lower health); negative → disintegration (higher)
  const nudge = sentimentScore >= 0 ? -magnitude : magnitude;

  const previousHealth = profile.healthLevel;
  const newHealth      = Math.max(HEALTH_MIN, Math.min(HEALTH_MAX, previousHealth + nudge));

  const direction: EvolutionDelta['direction'] =
    nudge < -0.001 ? 'integrating' :
    nudge >  0.001 ? 'disintegrating' :
    'stable';

  const updated: PersonalityProfile = {
    ...profile,
    healthLevel: parseFloat(newHealth.toFixed(3)),
    turnCount:   newTurnCount,
    lastUpdated: Date.now(),
  };

  return {
    profile: updated,
    delta: { previousHealth, newHealth, sentimentScore, nudge, direction },
  };
}

// ─── System prompt injection ──────────────────────────────────────────────────

/**
 * Generate the personality description block to prepend to the LLM system prompt.
 * Written in second person (describing Kili to the LLM) and kept to < 8 lines
 * to avoid ballooning the context window.
 */
export function getSystemPromptInsert(profile: PersonalityProfile): string {
  const typeDef = ENNEAGRAM_TYPES[profile.primaryType];
  const wingDef = profile.wing ? ENNEAGRAM_TYPES[profile.wing] : null;
  const band    = healthBand(profile.healthLevel);
  const desc    = typeDef.healthDescriptors[band];

  const typeLabel  = `Type ${profile.primaryType} – ${typeDef.name}`;
  const wingLabel  = wingDef ? ` with a ${wingDef.name} wing (${profile.primaryType}w${profile.wing})` : '';
  const healthLabel = `Health level ${profile.healthLevel.toFixed(1)}/9.0 (${band})`;

  const lines = [
    `## Kili's Personality`,
    `You are Kili, an AI with an Enneagram ${typeLabel}${wingLabel} personality.`,
    `Core motivation: ${typeDef.coreMotivation}.`,
    `Current psychological state: ${desc}.`,
    `Response style: ${typeDef.responseStyle}.`,
    `(${healthLabel} — ${band === 'integrated' ? 'express this type at its best' : band === 'disintegrated' ? 'you are under stress; this will colour your responses' : 'standard expression of type'})`,
    ``,
    `Apply this personality naturally through tone and framing. Do not announce or explain it.`,
  ];

  return lines.join('\n');
}

// ─── Avatar modifier generation ───────────────────────────────────────────────

/**
 * Compute the Three.js avatar modifiers for the current profile.
 * Called once at init and again after each evolvePersonality() call.
 *
 * @returns AvatarModifiers — consumed by applyPersonalityModifiers() in avatar-3d.ts
 */
export function getAvatarModifiers(profile: PersonalityProfile): AvatarModifiers {
  // Resting expression: blend primary + wing, then scale intensity by health
  const rawExpression = _effectiveRestingExpression(profile);
  const healthScale   = 0.6 + 0.4 * ((HEALTH_AVG - profile.healthLevel) / (HEALTH_MAX - HEALTH_MIN));
  // healthScale: health=1 → 1.0 (full expression), health=9 → 0.6 (subdued in stress)
  const restingAUs: Record<number, number> = {};
  for (const [au, intensity] of Object.entries(rawExpression)) {
    restingAUs[Number(au)] = Math.max(0, Math.min(1.0, intensity * healthScale));
  }

  // Hue: base cyan + blended type offset
  const hueOffset    = _effectiveHueOffset(profile);
  const absoluteHueDeg = ((BASE_CYAN_HUE + hueOffset) % 360 + 360) % 360;

  // Cognitive pace
  const cognitivePaceMult = _effectiveCognitivePace(profile);

  return { restingAUs, absoluteHueDeg, cognitivePaceMult };
}

/**
 * Convert AvatarModifiers.absoluteHueDeg to a THREE.Color-compatible hex number.
 * Exported so avatar-3d.ts can use it without importing hslToHex directly.
 */
export function modifiersToColorHex(modifiers: AvatarModifiers): number {
  return hslToHex(modifiers.absoluteHueDeg, BASE_SATURATION, BASE_LIGHTNESS);
}

// ─── Relationship dynamics (enhancement) ─────────────────────────────────────

/**
 * Cross-type relationship resonance table.
 * When Kili's type and the inferred user personality type (from user-personality.ts)
 * are known, this table nudges Kili's adaptation rate up or down.
 *
 * Based on classic Enneagram intertype dynamics (not spiritual doctrine — just
 * empirically observed communication compatibility).
 *
 * Format: `killiType_userType` → adaptationRateMultiplier
 * > 1.0 = resonance (quick mutual adaptation)
 * < 1.0 = tension (slower, more deliberate adaptation)
 */
export const TYPE_RESONANCE: Readonly<Record<string, number>> = {
  '2_4': 1.3, '4_2': 1.3,  // Helper ↔ Individualist: emotional depth resonance
  '1_3': 1.2, '3_1': 1.2,  // Reformer ↔ Achiever: shared excellence drive
  '5_1': 1.2, '1_5': 1.2,  // Investigator ↔ Reformer: analytical alignment
  '7_3': 1.2, '3_7': 1.2,  // Enthusiast ↔ Achiever: forward momentum
  '9_5': 0.8, '5_9': 0.8,  // Investigator ↔ Peacemaker: both withdraw → slow
  '8_2': 0.85,'2_8': 0.85, // Challenger ↔ Helper: power vs need tension
};

/**
 * Get an adapted evolution nudge that factors in cross-type resonance.
 * Pass `null` for userType when the user's type hasn't been inferred yet.
 */
export function getResonanceAdaptationRate(
  killiProfile:  PersonalityProfile,
  userTypeId:    EnneagramTypeId | null,
): number {
  const base = ENNEAGRAM_TYPES[killiProfile.primaryType].adaptationRate;
  if (!userTypeId) return base;
  const resonanceKey = `${killiProfile.primaryType}_${userTypeId}`;
  const multiplier   = TYPE_RESONANCE[resonanceKey] ?? 1.0;
  return Math.max(0.02, Math.min(0.10, base * multiplier));
}

// ─── Utility exports ──────────────────────────────────────────────────────────

/**
 * Get a human-readable summary of the current profile for debugging / dev UI.
 */
export function profileSummary(profile: PersonalityProfile): string {
  const typeDef = ENNEAGRAM_TYPES[profile.primaryType];
  const wingStr = profile.wing
    ? `w${profile.wing} (${ENNEAGRAM_TYPES[profile.wing].name})`
    : 'unwinged';
  const band    = healthBand(profile.healthLevel);

  return (
    `Type ${profile.primaryType} ${typeDef.name} ${wingStr} | ` +
    `Health: ${profile.healthLevel.toFixed(2)} (${band}) | ` +
    `Turns: ${profile.turnCount}`
  );
}

/** Check whether a profile is stale (> 30 days old). */
export function isProfileStale(profile: PersonalityProfile): boolean {
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  return (Date.now() - profile.lastUpdated) > THIRTY_DAYS_MS;
}

/**
 * Reset profile health to average — useful for a new session after a long
 * absence, or when the user explicitly resets their Kili personality.
 */
export function resetProfileHealth(profile: PersonalityProfile): PersonalityProfile {
  return { ...profile, healthLevel: HEALTH_AVG, lastUpdated: Date.now() };
}

// ─── Type AkiliPortal ambient declaration ─────────────────────────────────────
// Matches the runtime globals used by the existing codebase.
declare const AkiliPortal: {
  getToken?: () => string;
  getUser?:  () => { full_name?: string; name?: string } | null;
} | undefined;
